import { useMutation } from "@apollo/client";
import classNames from "classnames";
import { useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import Button from "@/components/Button";
import Chip, { ChipSet } from "@/components/Chip/Chip";
import Dialog from "@/components/Dialog";
import IconButton from "@/components/IconButton";
import TextField from "@/components/TextField";
import PgaAssistantIcon from "@/assets/images/pga-assistant.svg";
import pgaAssistantSvg from "@/assets/images/pga-assistant.svg?raw";
import { sysColors } from "@/assets/theme/colors";
import {
  CREATE_TASK_MUTATION,
  type BackofficeCRMCreateTaskInput,
  type CreateTaskResponse
} from "@/features/global/components/AddTaskSideSheet/mutations";
import {
  DEFAULT_QUICK_DUE_DATE_DAYS,
  QUICK_DUE_DATE_OPTIONS,
  isoDateDaysFromNow,
  quickDueDateChipLabel
} from "@/features/global/components/AddTaskSideSheet/quickDueDate";
import { addWorkdayTeamTag } from "@/features/global/components/AddTaskSideSheet/teamVisibilityOptions";
import useAmplitude from "@/hooks/useAmplitude";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import { useIsMobile } from "@/hooks/useIsMobile";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { TASKS_QUERY } from "@/pages/tasks/queries";
import { TASK_ANALYTICS_EVENTS, getCreatedTaskAnalyticsData } from "@/pages/tasks/_utils/amplitude";
import { TaskStageValues, TaskVisibilityValues } from "@/types";

const TASK_TAB_ID = "dashboard-quick-add-tab-task";
const ASK_TAB_ID = "dashboard-quick-add-tab-ask";
export const ASK_FAKE_DOOR_ANALYTICS_EVENT = "clicked AI ask fake door";
export const ASK_COMING_SOON_HEADLINE_REST = "may be coming soon...";
export const ASK_COMING_SOON_BODY =
  "Thanks for sharing your interest in an AI chat feature. We'll let you know when it's ready!";
export const ASK_COMING_SOON_DISMISS_LABEL = "Got it";

const ASK_GOLF_BALL_ICON_SRC = `data:image/svg+xml,${encodeURIComponent(
  pgaAssistantSvg.replace(/#111111/g, sysColors["sys-brand-primary"])
)}`;

function AskComingSoonHeadline() {
  return (
    <span>
      <span className="whitespace-nowrap text-sys-brand-primary">
        <img
          src={ASK_GOLF_BALL_ICON_SRC}
          alt=""
          data-testid="dashboard-quick-add-ask-coming-soon-icon"
          className="mr-1 inline-block size-[1.25em] align-[-0.15em]"
        />
        Ask
      </span>
      <span className="ml-0.5">{` ${ASK_COMING_SOON_HEADLINE_REST}`}</span>
    </span>
  );
}

function isImeComposing(event: { isComposing?: boolean; nativeEvent?: { isComposing?: boolean } }) {
  return event.isComposing === true || event.nativeEvent?.isComposing === true;
}

function composerChromeClassName(selected: boolean) {
  return classNames(
    "relative m-0 inline-flex items-center gap-1.5 rounded-t-lg border px-4 py-2.5 text-label-large",
    selected
      ? "-mb-px z-[1] border-sys-brand-outline-variant border-b-sys-brand-surface bg-sys-brand-surface text-sys-brand-on-surface"
      : "z-0 border-transparent bg-sys-brand-surface-container-high text-sys-brand-on-surface-variant"
  );
}

function TaskComposerTab() {
  return (
    <span data-testid={TASK_TAB_ID} className={composerChromeClassName(true)}>
      Task
    </span>
  );
}

function AskFakeDoor({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      data-testid={ASK_TAB_ID}
      aria-label="Ask (coming soon)"
      onClick={onClick}
      className={classNames(composerChromeClassName(false), "cursor-pointer")}
    >
      <img src={PgaAssistantIcon} alt="" className="h-4 w-4" />
      Ask
    </button>
  );
}

export default function QuickAddTask() {
  const { trackEvent } = useAmplitude();
  const currentUser = useCurrentBackofficeUser();
  const isMobile = useIsMobile();
  const formRef = useRef<HTMLFormElement>(null);
  const submittingRef = useRef(false);

  const [title, setTitle] = useState("");
  const [selectedDays, setSelectedDays] = useState<number>(DEFAULT_QUICK_DUE_DATE_DAYS);
  const [nameError, setNameError] = useState<string | undefined>();
  const [askComingSoonOpen, setAskComingSoonOpen] = useState(false);

  const [createTask, { loading: createLoading }] = useMutation<CreateTaskResponse>(CREATE_TASK_MUTATION, {
    refetchQueries: [TASKS_QUERY]
  });

  const trimmedTitle = title.trim();
  const canSubmit = Boolean(trimmedTitle && currentUser?.id && !createLoading);

  async function submitTask() {
    if (createLoading || submittingRef.current) return;

    if (!trimmedTitle) {
      setNameError("Task name is required");
      return;
    }

    if (!currentUser?.id) {
      showErrorSnackbar("You must be signed in to add a task.");
      return;
    }

    const dueDate = isoDateDaysFromNow(selectedDays);
    const teamTags = addWorkdayTeamTag([], currentUser.department);

    const input: BackofficeCRMCreateTaskInput = {
      name: trimmedTitle,
      description: "",
      assignedToId: currentUser.id,
      visibility: TaskVisibilityValues.PUBLIC
    };

    if (dueDate) {
      input.dueDate = dueDate;
    }

    if (teamTags.length > 0) {
      input.teamTags = teamTags;
    }

    submittingRef.current = true;

    try {
      const result = await createTask({ variables: { input } });

      if (result.data?.createTask?.success) {
        const taskId = result.data.createTask.task?.id;
        trackEvent(
          TASK_ANALYTICS_EVENTS.created,
          getCreatedTaskAnalyticsData({
            taskId,
            source: "dashboard_quick_add",
            stage: TaskStageValues.OPEN,
            visibility: TaskVisibilityValues.PUBLIC,
            dueDate,
            hasAssignee: true,
            teamTagCount: teamTags.length
          })
        );
        showSnackbar("Task created");
        setTitle("");
        setSelectedDays(DEFAULT_QUICK_DUE_DATE_DAYS);
        setNameError(undefined);
        return;
      }

      showErrorSnackbar(result.data?.createTask?.message || "Failed to create task");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error creating task. Please try again.";
      showErrorSnackbar(errorMessage);
    } finally {
      submittingRef.current = false;
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submitTask();
  }

  function handleComposerKeyDown(event: KeyboardEvent<HTMLElement>) {
    const composing = isImeComposing(event);

    if (event.key !== "Enter" || event.shiftKey || composing) {
      return;
    }

    event.preventDefault();
    if (canSubmit) {
      formRef.current?.requestSubmit();
    }
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-3" data-testid="dashboard-quick-add-task">
      <form ref={formRef} className="flex w-full min-w-0 flex-col gap-3" onSubmit={handleSubmit}>
        <div
          className="overflow-hidden rounded-lg border border-extended-pgadarknavy-brand-color bg-sys-brand-surface"
          data-testid="dashboard-quick-add-card"
        >
          <div
            data-testid="dashboard-quick-add-tablist"
            className="flex items-end gap-0 border-b border-sys-brand-outline-variant bg-sys-brand-surface-container-high px-3 pt-1"
          >
            <TaskComposerTab />
            <AskFakeDoor
              onClick={() => {
                trackEvent(ASK_FAKE_DOOR_ANALYTICS_EVENT);
                setAskComingSoonOpen(true);
              }}
            />
          </div>
          <div className="p-3">
            <TextField
              className="w-full min-w-0 [--md-outlined-text-field-input-text-placeholder-color:var(--md-sys-color-outline)] [&_md-outlined-text-field]:w-full [&_md-outlined-text-field]:[--md-outlined-text-field-input-text-placeholder-color:var(--md-sys-color-outline)]"
              disabled={createLoading || !currentUser?.id}
              error={!!nameError}
              errorText={nameError}
              placeholder="Add a new task..."
              value={title}
              onChangeText={(value) => {
                setTitle(value);
                if (nameError) setNameError(undefined);
              }}
              onKeyDown={handleComposerKeyDown}
            />
          </div>
          <div
            className="flex w-full min-w-0 flex-nowrap items-center gap-3 border-t border-sys-brand-outline-variant bg-sys-brand-surface-container-low px-3 py-2"
            data-testid="dashboard-quick-add-footer"
          >
            <div
              className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-visible"
              data-testid="dashboard-quick-add-due-row"
            >
              <p id="quick-add-task-due-label" className="shrink-0 text-label-medium text-sys-brand-on-surface-variant">
                Due
              </p>
              <div className="min-w-0 flex-1 overflow-visible [&_md-chip-set]:flex-nowrap [&_md-chip-set]:overflow-visible">
                <ChipSet aria-labelledby="quick-add-task-due-label">
                  {QUICK_DUE_DATE_OPTIONS.map((option) => (
                    <Chip
                      key={option.days}
                      color="primary"
                      label={quickDueDateChipLabel(option, isMobile)}
                      showCheckmark={false}
                      selected={selectedDays === option.days}
                      disabled={createLoading || !currentUser?.id}
                      onClick={() => setSelectedDays(option.days)}
                    />
                  ))}
                </ChipSet>
              </div>
            </div>
            <IconButton
              name="check"
              variant="filled"
              skipMargin
              containerClassName="ml-1 shrink-0"
              title="Save"
              ariaLabel="Save"
              disabled={!canSubmit}
              data-testid="dashboard-quick-add-task-submit"
              type="button"
              onClick={() => {
                if (canSubmit) {
                  formRef.current?.requestSubmit();
                }
              }}
            />
          </div>
        </div>
      </form>
      <Dialog
        open={askComingSoonOpen}
        onClosed={() => setAskComingSoonOpen(false)}
        headline={<AskComingSoonHeadline />}
        actions={
          <Button
            variant="filled"
            testId="dashboard-quick-add-ask-coming-soon-dismiss"
            onClick={() => setAskComingSoonOpen(false)}
          >
            {ASK_COMING_SOON_DISMISS_LABEL}
          </Button>
        }
      >
        <p className="text-body-medium text-sys-brand-on-surface">{ASK_COMING_SOON_BODY}</p>
      </Dialog>
    </div>
  );
}
